import React, { useRef, useState, useEffect } from "react";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { bottom, top, left, right } from "@popperjs/core";
import { usePopper } from "react-popper";
import { classNames } from "../../helpers/classNames";

const SortDropdown = ({
  buttonClasses,
  icon,
  label,
  xPlacement = "",
  data = [],
  defaultSort = null, // Add this prop to control default selection
  ...props
}) => {
  const popperElRef = useRef(null);
  const [targetElement, setTargetElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const [sortName, setSortName] = useState("");
  const [sortIcon, setSortIcon] = useState("");

  const placements = {
    bottom: bottom,
    bottomLeft: "bottom-start",
    bottomRight: "bottom-end",
    top: top,
    topLeft: "top-start",
    topRight: "top-end",
    right: right,
    left: left,
    "": bottom,
  };

  const { styles, attributes } = usePopper(targetElement, popperElement, {
    placement: placements[xPlacement],
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 8],
        },
      },
    ],
  });

  // Set default sort only once when component mounts or data changes
  useEffect(() => {
    if (data?.length > 0 && !isInitialized) {
      const defaultItem = defaultSort
        ? data.find((item) => item.label === defaultSort) || data[0]
        : data[0];

      setSortName(defaultItem.label);
      setSortIcon(defaultItem.icon);
      setIsInitialized(true);

      // Only call click if it's not the initial render to avoid infinite loop
      // The parent component should handle the initial state
    }
  }, [data, defaultSort, isInitialized]);

  // Reset initialization when data changes significantly
  useEffect(() => {
    if (data?.length === 0) {
      setIsInitialized(false);
      setSortName("");
      setSortIcon("");
    }
  }, [data]);

  const handleItemClick = (item) => {
    setSortName(item.label);
    setSortIcon(item.icon);
    if (item.click) {
      item.click();
    }
  };

  return (
    <Menu as="div">
      {({ open }) => (
        <>
          <MenuButton
            ref={setTargetElement}
            className={classNames(
              "relative overflow-hidden flex justify-center items-center bg-magenta-500 text-white rounded-lg text-lg h-10 py-0 px-2 transition-all duration-200 min-w-[40px]",
              buttonClasses
            )}
          >
            <div className="flex items-center gap-2">
              {icon && (
                <i className={classNames("fa-fw", sortIcon || icon)}></i>
              )}
              {label && (
                <>
                  {label}
                  {sortName && (
                    <span className="inline-block text-lg transition-all duration-200 whitespace-nowrap font-semibold">
                      : {sortName}
                    </span>
                  )}
                </>
              )}
            </div>
          </MenuButton>

          <Transition
            show={open}
            leave="transition duration-75"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            className="z-50"
            beforeEnter={() => setPopperElement(popperElRef.current)}
            afterLeave={() => setPopperElement(null)}
            ref={popperElRef}
            style={styles.popper}
            {...attributes.popper}
          >
            <MenuItems className="w-60 origin-top-right bg-white border-0 divide-y divide-slate-100 rounded-md shadow ring-0">
              {data.length > 0 && (
                <div className="py-2 px-2 max-h-80 space-y-2 pb-4 overflow-auto scroll-smooth scrollbar">
                  {data.map((item, index) => (
                    <MenuItem
                      key={index}
                      as="button"
                      onClick={() => handleItemClick(item)}
                      className={({ active }) =>
                        classNames(
                          "group flex w-full items-center rounded px-2 py-1.5 text-base font-medium space-x-2 transition-all duration-200",
                          active
                            ? "bg-magenta-500 text-primary"
                            : "text-slate-900",
                          sortName === item.label ? "bg-magenta-100" : ""
                        )
                      }
                    >
                      <>
                        {item.icon && (
                          <i className={classNames("fa-fw", item.icon)}></i>
                        )}
                        {item.label && (
                          <span className="inline-block text-sm">
                            {item.label}
                          </span>
                        )}
                      </>
                    </MenuItem>
                  ))}
                </div>
              )}
            </MenuItems>
          </Transition>
        </>
      )}
    </Menu>
  );
};

export default SortDropdown;
